/**
 * Interpolação da grade do modelo — funções puras (testadas em
 * `previsao.test.ts`). A MESMA regra vale para o mapa e para a ficha do
 * ponto: bilinear entre os quatro pontos vizinhos, com os que faltam (NaN)
 * fora da conta e o peso dos presentes reponderado; sem nenhum, NaN.
 *
 * A grade marca o CENTRO de cada ponto: linha i → lat0 − i·passo, coluna j →
 * lon0 + j·passo. A imagem do mapa cobre meia célula além dos pontos da
 * borda (ver `extensaoLonLat`).
 */

export interface Grade {
  lat0: number;
  lon0: number;
  passo: number;
  ni: number;
  nj: number;
}

/** Valor no ponto fracionário (fi = linha, fj = coluna), grampeado na borda. */
export function bilinear(v: ArrayLike<number>, g: Pick<Grade, 'ni' | 'nj'>, fi: number, fj: number): number {
  const i = Math.min(Math.max(fi, 0), g.ni - 1);
  const j = Math.min(Math.max(fj, 0), g.nj - 1);
  // O último par de pontos começa em n − 2 (na borda de baixo/direita, t = 1).
  const i0 = Math.min(Math.floor(i), Math.max(g.ni - 2, 0));
  const j0 = Math.min(Math.floor(j), Math.max(g.nj - 2, 0));
  const ti = g.ni > 1 ? i - i0 : 0;
  const tj = g.nj > 1 ? j - j0 : 0;
  let soma = 0;
  let peso = 0;
  const somar = (ii: number, jj: number, w: number) => {
    if (w <= 0 || ii >= g.ni || jj >= g.nj) return;
    const x = v[ii * g.nj + jj];
    if (Number.isNaN(x)) return;
    soma += x * w;
    peso += w;
  };
  somar(i0, j0, (1 - ti) * (1 - tj));
  somar(i0, j0 + 1, (1 - ti) * tj);
  somar(i0 + 1, j0, ti * (1 - tj));
  somar(i0 + 1, j0 + 1, ti * tj);
  if (peso > 0) return soma / peso;
  // Exatamente sobre um ponto sem dado (peso 0 nos outros): vazio.
  return NaN;
}

/** Linha e coluna fracionárias de um lon/lat na grade. */
export function posicaoNaGrade(g: Grade, lon: number, lat: number): [number, number] {
  return [(g.lat0 - lat) / g.passo, (lon - g.lon0) / g.passo];
}

/** Valor no lon/lat (NaN fora da grade ou sem dado). */
export function valorNoPonto(v: ArrayLike<number>, g: Grade, lon: number, lat: number): number {
  const [fi, fj] = posicaoNaGrade(g, lon, lat);
  if (fi < -0.5 || fj < -0.5 || fi > g.ni - 0.5 || fj > g.nj - 0.5) return NaN;
  return bilinear(v, g, fi, fj);
}

/** Extensão lon/lat [oeste, sul, leste, norte] da imagem: os pontos e meia célula em volta. */
export function extensaoLonLat(g: Grade): [number, number, number, number] {
  const h = g.passo / 2;
  return [g.lon0 - h, g.lat0 - (g.ni - 1) * g.passo - h, g.lon0 + (g.nj - 1) * g.passo + h, g.lat0 + h];
}

/**
 * Grade `fator` vezes mais fina, cobrindo `extensaoLonLat`: o pixel (y, x) da
 * saída tem centro em fi = (y + 0.5)/fator − 0.5 (idem coluna).
 */
export function ampliar(v: ArrayLike<number>, g: Pick<Grade, 'ni' | 'nj'>, fator: number): Float32Array {
  const ni = g.ni * fator;
  const nj = g.nj * fator;
  const out = new Float32Array(ni * nj);
  for (let y = 0; y < ni; y++) {
    const fi = (y + 0.5) / fator - 0.5;
    for (let x = 0; x < nj; x++) out[y * nj + x] = bilinear(v, g, fi, (x + 0.5) / fator - 0.5);
  }
  return out;
}
